//Built-in libraries

//Third party libraries
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
//Custom components
import ProjectSourcesHookFieldArray, { projectSourcesValidationSchema } from '@/components/Sherpa/Projects/ProjectSourcesHookFieldArray';
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import Modal from '@/components/UI/Modal';
//Services
import { patchProject } from '../../../../../../../api/Sherpa/Project';
import type { IPatchProjectServicePayload } from '../../../../../../../api/Sherpa/Project/modules/ProjectCore/types';
//Assets andd Miscellaneous
import { SherpaProjectReactQueryKey } from '../../../../../../../constants/ReactQueryKeys';
import { invalidateDashboardWidgetsData } from '../../../../../../../hooks/ReactQueryHooks/useDashboardQuery';
import { useProject } from '../../../../../../../hooks/ReactQueryHooks/useProjectQuery';
import { notifyUser } from '../../../../../../../store/Toast';
import type { ProjectSourceInterface } from '../../../../../../../types/Project';
import { formatNumber, projectBudgetTotal, sanitizeResponseError } from '../../../../../../../utils';

//Component Begin

interface IUpdateProjectSourcesProps {
  handleModal: () => void;
}

export default function UpdateProjectSources({ handleModal }: IUpdateProjectSourcesProps) {
  const { projectId } = useParams<string>();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const fetchProject = useProject({ projectId: parseInt(projectId!, 10) });
  const sources = fetchProject?.data?.data?.sources;
  const uses = fetchProject?.data?.data?.uses;

  const updateProjectSources = useMutation(
    (data: Partial<IPatchProjectServicePayload>) => patchProject({ projectId: Number(projectId), data }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([SherpaProjectReactQueryKey.GET_PROJECT]);
        invalidateDashboardWidgetsData(queryClient);
        queryClient.refetchQueries([SherpaProjectReactQueryKey.GET_PROJECT]);
        handleModal();
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Project sources update successful',
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const onSubmit = (formData: { sources: ProjectSourceInterface[] }) => {
    const sourcesTotal = projectBudgetTotal(formData.sources);
    const usesTotal = projectBudgetTotal(uses || []);
    if (usesTotal > sourcesTotal) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: `Sources total must be higher or equal to your uses total amount of $${formatNumber(usesTotal)}`,
          toastType: 'failed',
        })
      );
    } else {
      updateProjectSources.mutate(formData);
    }
  };

  return (
    <Modal>
      <HookFormProvider defaultValues={{ sources }} resolver={yupResolver(projectSourcesValidationSchema) as any} mode='all'>
        <HookForm onSubmit={onSubmit}>
          <h2 className='mt-0'>Project Sources</h2>
          <ProjectSourcesHookFieldArray />
          <div className='flex justify-end gap-4 mt-5'>
            <Button label='Cancel' btnStyle='btn-neutral' type='button' onClick={handleModal} />
            <Button
              disabled={updateProjectSources.isLoading}
              label={`${updateProjectSources.isLoading ? 'Saving...' : 'Save'}`}
              btnStyle='btn-primary'
              type='submit'
            />
          </div>
        </HookForm>
      </HookFormProvider>
    </Modal>
  );
}
